import { useEffect, useRef } from 'react'

import { datadogRum } from '@datadog/browser-rum'

import {
  isCampaignBannerRecipeItem,
  type CampaignBannerHeaderContentBlock,
  type CompactCampaignBannerHeaderContentBlock,
  type MenuContentBlock,
  type MenuResponse,
  type RecipeGridContentBlock,
} from '@library/api-menu-experience'
import type { CollectionSlug, CollectionUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import type {
  BrowseSelectionState,
  LoadMerchBannerEvent,
  MerchHeaderType,
  ViewRecipeCollectionEvent,
} from 'routes/Menu/MenuExperience/types'

type CampaignBannerContentBlock =
  | CampaignBannerHeaderContentBlock
  | CompactCampaignBannerHeaderContentBlock

type UseMenuExperienceTrackingArgs = {
  basketCount: number
  browseSelectionState: BrowseSelectionState
  clearBrowseSelectionState: () => void
  data: MenuResponse
  deliveryDate?: string
}

const isCampaignBanner = (
  contentBlock: MenuContentBlock,
): contentBlock is CampaignBannerContentBlock =>
  contentBlock.type === 'campaign-banner' || contentBlock.type === 'compact-campaign-banner'

const getMerchHeaderType = (content: MenuContentBlock[]): MerchHeaderType | undefined =>
  content.find(
    ({ type }) =>
      type === 'category-branding' ||
      type === 'campaign-banner' ||
      type === 'compact-campaign-banner',
  )?.type as MerchHeaderType | undefined

const getRecipeGrid = (content: MenuContentBlock[]): RecipeGridContentBlock | undefined =>
  content.find(({ type }) => type === 'recipe-grid') as RecipeGridContentBlock | undefined

const getParentCategoriesCount = (content: MenuContentBlock[]) => {
  const categoryGrid = content.find(({ type }) => type === 'category-grid')

  return categoryGrid?.type === 'category-grid' ? categoryGrid.parentCategories.length : 0
}

const getCampaignTarget = (banner: CampaignBannerContentBlock) => {
  if (banner.type === 'compact-campaign-banner') {
    return {
      id: banner.targetCategoryId,
      slug: banner.targetCategorySlug,
    }
  }

  const cta = banner.items.find(
    (item): item is Extract<CampaignBannerHeaderContentBlock['items'][number], { type: 'cta' }> =>
      item.type === 'cta',
  )

  return cta
    ? {
        id: cta.targetCategoryId,
        slug: cta.targetCategorySlug,
      }
    : undefined
}

const getCampaignRecipeIds = (banner: CampaignBannerContentBlock) =>
  banner.items.flatMap((item) => (isCampaignBannerRecipeItem(item) ? [item.id] : []))

export const useMenuExperienceTracking = ({
  basketCount,
  browseSelectionState,
  clearBrowseSelectionState,
  data,
  deliveryDate,
}: UseMenuExperienceTrackingArgs) => {
  const lastTrackedCollectionIdRef = useRef<string | null>(null)
  const trackedMerchBannerKeysRef = useRef<Set<string>>(new Set())
  const recipeGrid = getRecipeGrid(data.content)
  const campaignBanner = data.content.find(isCampaignBanner)
  const isCampaignBannerVisible = Boolean(campaignBanner)
  const merchHeaderType = getMerchHeaderType(data.content)
  const isBrowse = data.type === 'browse'
  const recipeIds = recipeGrid?.items.map(({ id }) => id) ?? []
  const originalOrder = recipeIds.join(',')
  const parentCategoriesCount = getParentCategoriesCount(data.content)

  useEffect(() => {
    if (!isBrowse || parentCategoriesCount === 0) {
      return
    }

    datadogRum?.addAction('view_browse_screen', {
      deliveryDate,
      parentCategoriesCount,
    })
  }, [deliveryDate, isBrowse, parentCategoriesCount])

  useEffect(() => {
    if (isBrowse) {
      return
    }

    datadogRum?.addAction('view_collection_screen', {
      deliveryDate,
      collectionId: data.categoryId,
      recipeCount: recipeGrid?.recipeCount ?? 0,
      merchandisingBannerShown: isCampaignBannerVisible,
    })
  }, [data.categoryId, deliveryDate, isBrowse, isCampaignBannerVisible, recipeGrid?.recipeCount])

  useEffect(() => {
    if (isBrowse) {
      lastTrackedCollectionIdRef.current = null
      trackedMerchBannerKeysRef.current.clear()

      return
    }

    if (lastTrackedCollectionIdRef.current === data.categoryId) {
      return
    }

    const isBrowseChildSelection = browseSelectionState?.childCollectionId === data.categoryId

    queueTrackingEvent<ViewRecipeCollectionEvent>({
      action: 'view_recipe_collection',
      properties: {
        collection_id: data.categoryId as CollectionUUID,
        collection_slug: data.categorySlug as CollectionSlug,
        menu_uuid: data.menuId,
        basket_count: basketCount,
        original_order: originalOrder,
        ...(merchHeaderType && { merch_header_type: merchHeaderType }),
        ...(isBrowseChildSelection && {
          parent_collection_id: browseSelectionState.parentCollectionId,
        }),
      },
    })

    lastTrackedCollectionIdRef.current = data.categoryId
    clearBrowseSelectionState()
  }, [
    basketCount,
    browseSelectionState,
    clearBrowseSelectionState,
    data.categoryId,
    data.categorySlug,
    data.menuId,
    isBrowse,
    merchHeaderType,
    originalOrder,
  ])

  useEffect(() => {
    if (isBrowse || !campaignBanner) {
      return
    }

    const target = getCampaignTarget(campaignBanner)

    if (!target) {
      return
    }

    const merchBannerKey = [data.menuId, data.categoryId, campaignBanner.type, target.id].join(':')

    if (trackedMerchBannerKeysRef.current.has(merchBannerKey)) {
      return
    }

    queueTrackingEvent<LoadMerchBannerEvent>({
      action: 'load_merch_banner',
      properties: {
        action: 'load',
        current_collection_id: data.categoryId as CollectionUUID,
        current_collection_slug: data.categorySlug as CollectionSlug,
        initial_view_flag: true,
        menu_uuid: data.menuId,
        merch_header_type: campaignBanner.type,
        target_collection_id: target.id,
        target_collection_slug: target.slug,
        recipe_uuids_shown: getCampaignRecipeIds(campaignBanner),
      },
    })

    trackedMerchBannerKeysRef.current.add(merchBannerKey)
  }, [campaignBanner, data.categoryId, data.categorySlug, data.menuId, isBrowse])
}
